import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  Download, 
  Calendar, 
  FileText, 
  TrendingUp, 
  Users,
  Activity,
  Target,
  Trophy,
  MapPin,
  Crown,
  Loader,
  RefreshCw,
  Eye
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const ReportsAnalytics = () => {
  const [selectedReport, setSelectedReport] = useState('user-activity');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    to: new Date().toISOString().split('T')[0] // today
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [quickStats, setQuickStats] = useState(null);

  const reports = [
    {
      id: 'user-activity',
      title: 'User Activity Report',
      description: 'Comprehensive overview of user engagement and activity patterns',
      icon: TrendingUp,
      endpoint: '/reports/user-activity'
    },
    {
      id: 'group-performance',
      title: 'Group Performance',
      description: 'Analysis of group activities and member participation',
      icon: BarChart3,
      endpoint: '/reports/group-performance'
    },
    {
      id: 'challenge-success',
      title: 'Challenge Success Rate',
      description: 'Track challenge completion rates and user engagement',
      icon: Target,
      endpoint: '/reports/challenge-success'
    }
  ];

  useEffect(() => {
    fetchQuickStats();
  }, []);

  useEffect(() => {
    if (previewMode) {
      generateReport();
    }
  }, [selectedReport, dateRange, previewMode]);

  const fetchQuickStats = async () => {
    try {
      const response = await axios.get('/reports/stats');
      setQuickStats(response.data);
    } catch (error) {
      console.error('Error fetching quick stats:', error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const currentReport = reports.find(r => r.id === selectedReport);
      const params = new URLSearchParams({
        date_from: dateRange.from,
        date_to: dateRange.to
      });
      
      const response = await axios.get(`${currentReport.endpoint}?${params}`);
      setReportData(response.data);
      setPreviewMode(true);
    } catch (error) {
      console.error('Error generating report:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const params = new URLSearchParams({
        date_from: dateRange.from,
        date_to: dateRange.to,
        format: format
      });
      
      // For now, generate a simple CSV export
      if (format === 'csv' && reportData) {
        downloadCSV();
      } else {
        alert(`Export as ${format.toUpperCase()} functionality will be implemented here. Data is ready for export!`);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error exporting report. Please try again.');
    }
  };

  const downloadCSV = () => {
    if (!reportData) return;
    
    let csvContent = '';
    const currentReport = reports.find(r => r.id === selectedReport);
    
    // Generate CSV based on report type
    if (selectedReport === 'user-activity') {
      csvContent = 'Date,Activity Count,Active Users,Total Steps,Total Distance,Total Calories\n';
      reportData.dailyTrends?.forEach(day => {
        csvContent += `${day.date},${day.activity_count},${day.active_users},${day.total_steps},${day.total_distance},${day.total_calories}\n`;
      });
    } else if (selectedReport === 'group-performance') {
      csvContent = 'Group Name,City,Members,Activities,Total Steps,Total Distance,Total Calories\n';
      reportData.topGroups?.forEach(group => {
        csvContent += `${group.name},${group.city},${group.member_count},${group.activity_count},${group.total_steps},${group.total_distance},${group.total_calories}\n`;
      });
    } else if (selectedReport === 'challenge-success') {
      csvContent = 'Challenge Title,Type,Participants,Completed,Completion Rate\n';
      reportData.challengeDetails?.forEach(challenge => {
        csvContent += `${challenge.title},${challenge.type},${challenge.total_participants},${challenge.completed_count},${challenge.completion_rate}%\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentReport.title.replace(/\s+/g, '_')}_${dateRange.from}_to_${dateRange.to}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const chartColors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6'
  };

  const renderReportPreview = () => {
    if (!reportData) {
      return (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <BarChart3 className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            {reports.find(r => r.id === selectedReport)?.title}
          </h4>
          <p className="text-gray-600 mb-4">
            Select date range and click "Generate Preview" to view the report
          </p>
          <button 
            onClick={generateReport}
            disabled={loading}
            className="btn-primary flex items-center mx-auto"
          >
            {loading ? (
              <>
                <Loader className="h-5 w-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Eye className="h-5 w-5 mr-2" />
                Generate Preview
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Report Header */}
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold text-gray-900">
              {reports.find(r => r.id === selectedReport)?.title}
            </h4>
            <p className="text-gray-600">
              {reportData?.dateRange?.from && reportData?.dateRange?.to 
                ? `${formatDate(reportData.dateRange.from)} - ${formatDate(reportData.dateRange.to)}`
                : `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
              }
            </p>
          </div>
          <button 
            onClick={generateReport}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>

        {/* Report Content */}
        {selectedReport === 'user-activity' && renderUserActivityReport()}
        {selectedReport === 'group-performance' && renderGroupPerformanceReport()}
        {selectedReport === 'challenge-success' && renderChallengeSuccessReport()}
      </div>
    );
  };

  const renderUserActivityReport = () => {
    if (!reportData || !reportData.summary) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Active Users</p>
                <p className="text-2xl font-bold text-blue-900">{reportData.summary?.total_users || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Total Activities</p>
                <p className="text-2xl font-bold text-green-900">{reportData.summary?.total_activities || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Total Steps</p>
                <p className="text-2xl font-bold text-orange-900">{parseInt(reportData.summary?.total_steps || 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Total Distance</p>
                <p className="text-2xl font-bold text-purple-900">{parseFloat(reportData.summary?.total_distance || 0).toFixed(1)} km</p>
              </div>
              <MapPin className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Daily Activity Trends */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h5 className="text-lg font-semibold text-gray-900 mb-4">Daily Activity Trends</h5>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={reportData.dailyTrends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => formatDate(value)}
                  formatter={(value, name) => [
                    name === 'total_steps' ? value.toLocaleString() : value,
                    name === 'total_steps' ? 'Steps' : 
                    name === 'active_users' ? 'Active Users' : 
                    'Activities'
                  ]}
                />
                <Line type="monotone" dataKey="total_steps" stroke={chartColors.primary} strokeWidth={2} />
                <Line type="monotone" dataKey="active_users" stroke={chartColors.secondary} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Users and City Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Users */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h5>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {(reportData.topUsers || []).map((user, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 flex items-center">
                        {user.name}
                        {user.membership_type === 'premium' && <Crown className="h-4 w-4 ml-1 text-yellow-500" />}
                      </p>
                      <p className="text-sm text-gray-500">{user.city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{parseInt(user.total_steps).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">steps</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* City Performance */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Activity by City</h5>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.cityActivity?.slice(0, 5) || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="city" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      value.toLocaleString(),
                      name === 'total_steps' ? 'Steps' : 'Activities'
                    ]}
                  />
                  <Bar dataKey="total_steps" fill={chartColors.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupPerformanceReport = () => {
    if (!reportData || !reportData.summary) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Groups</p>
                <p className="text-2xl font-bold text-blue-900">{reportData.summary.total_groups}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Total Members</p>
                <p className="text-2xl font-bold text-green-900">{reportData.summary.total_members}</p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Avg Members/Group</p>
                <p className="text-2xl font-bold text-orange-900">{parseFloat(reportData.summary.avg_members_per_group || 0).toFixed(1)}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-600" />
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Group Activities</p>
                <p className="text-2xl font-bold text-purple-900">{reportData.summary.total_activities}</p>
              </div>
              <Activity className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Group Trends */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h5 className="text-lg font-semibold text-gray-900 mb-4">Group Activity Trends</h5>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={reportData.groupTrends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => formatDate(value)}
                  formatter={(value, name) => [
                    value,
                    name === 'active_groups' ? 'Active Groups' : 'Total Activities'
                  ]}
                />
                <Area type="monotone" dataKey="active_groups" stroke={chartColors.primary} fill={chartColors.primary} fillOpacity={0.3} />
                <Area type="monotone" dataKey="total_activities" stroke={chartColors.secondary} fill={chartColors.secondary} fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Groups Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h5 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Groups</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Steps</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance (km)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(reportData.topGroups || []).map((group, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{group.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.city}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.member_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.activity_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{parseInt(group.total_steps).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{parseFloat(group.total_distance).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderChallengeSuccessReport = () => {
    if (!reportData || !reportData.summary) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Challenges</p>
                <p className="text-2xl font-bold text-blue-900">{reportData.summary.total_challenges}</p>
              </div>
              <Target className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Participants</p>
                <p className="text-2xl font-bold text-green-900">{reportData.summary.total_participants}</p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Completed</p>
                <p className="text-2xl font-bold text-orange-900">{reportData.summary.completed_participants}</p>
              </div>
              <Trophy className="h-8 w-8 text-orange-600" />
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Success Rate</p>
                <p className="text-2xl font-bold text-purple-900">{parseFloat(reportData.summary.completion_rate || 0).toFixed(1)}%</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Challenge Type Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Performance by Type</h5>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.typePerformance || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value}%`,
                      'Completion Rate'
                    ]}
                  />
                  <Bar dataKey="completion_rate" fill={chartColors.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Challenge Distribution</h5>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reportData.typePerformance || []}
                    dataKey="challenge_count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {(reportData.typePerformance || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={Object.values(chartColors)[index % Object.values(chartColors).length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Challenge Details Table */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h5 className="text-lg font-semibold text-gray-900 mb-4">Challenge Details</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Challenge</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Success Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(reportData.challengeDetails || []).map((challenge, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{challenge.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{challenge.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{challenge.total_participants}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{challenge.completed_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        challenge.completion_rate >= 70 ? 'bg-green-100 text-green-800' :
                        challenge.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {parseFloat(challenge.completion_rate || 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Reports & Analytics</h2>
        <p className="text-gray-600 mt-2">Generate comprehensive insights and export detailed reports</p>
      </div>

      {/* Quick Stats - Only show if there's data */}
      {quickStats?.hasData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Activities Tracked</p>
                <p className="text-3xl font-bold text-white">{quickStats?.totalReports?.toLocaleString() || 0}</p>
              </div>
              <FileText className="h-12 w-12 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">This Month</p>
                <p className="text-3xl font-bold text-white">{quickStats?.monthlyReports?.toLocaleString() || 0}</p>
              </div>
              <Calendar className="h-12 w-12 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Most Popular Report</p>
                <p className="text-lg font-bold text-white">{quickStats?.mostPopular || 'User Activity'}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-yellow-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Preferred Export</p>
                <p className="text-lg font-bold text-white">{quickStats?.preferredFormat || 'CSV'}</p>
              </div>
              <Download className="h-12 w-12 text-purple-200" />
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {quickStats && !quickStats.hasData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 mb-2">No Data Available</h3>
          <p className="text-yellow-700">
            There's no activity data in the database yet. Quick stats will appear once you have users and activities.
          </p>
        </div>
      )}

      {/* Report Selection */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Report Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reports.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`text-left p-4 border rounded-lg transition-all ${
                  selectedReport === report.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-8 w-8 mb-3 ${
                  selectedReport === report.id ? 'text-blue-600' : 'text-gray-500'
                }`} />
                <h4 className="font-medium text-gray-900 mb-2">{report.title}</h4>
                <p className="text-sm text-gray-600">{report.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date Range and Export Options */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>

          {previewMode && reportData && (
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => exportReport('excel')}
              className="btn-primary flex items-center"
            >
              <Download className="h-5 w-5 mr-2" />
              Export as Excel
            </button>
{/* <button
              onClick={() => exportReport('pdf')}
              className="btn-secondary flex items-center"
            >
              <FileText className="h-5 w-5 mr-2" />
              Export as PDF
              </button> */}
            <button
              onClick={() => exportReport('csv')}
              className="btn-secondary flex items-center"
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              Export as CSV
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Report Preview */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Preview</h3>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        ) : (
          renderReportPreview()
        )}
      </div>
    </div>
  );
};

export default ReportsAnalytics; 